import { lazy, Suspense, useRef, useState } from 'react';
import type { SectionId } from './sections/types';
import { SECTIONS } from './sections/types';
import { SectionNav } from './sections/SectionNav';
import { HomeSection } from './sections/HomeSection';
import { SectionFallback } from './SectionFallback';
import { markStatsTabViewed, markCodexTabViewed } from '../sectionBadges';
import { useSwipeNav } from './useSwipeNav';

// Phase 3g bundle hardening: HomeSection stays eager (default-active landing).
// All other 7 sections lazy via React.lazy — each emits its own JS chunk that
// downloads on first tab-tap. Chunks the named exports through default-shim
// arrow so we don't have to touch the section files (they keep named exports).
const StatsSection     = lazy(() => import('./sections/StatsSection').then((m) => ({ default: m.StatsSection })));
const HideoutSection   = lazy(() => import('./sections/HideoutSection').then((m) => ({ default: m.HideoutSection })));
const SectorsSection   = lazy(() => import('./sections/SectorsSection').then((m) => ({ default: m.SectorsSection })));
const InventorySection = lazy(() => import('./sections/InventorySection').then((m) => ({ default: m.InventorySection })));
const LoadoutSection   = lazy(() => import('./sections/LoadoutSection').then((m) => ({ default: m.LoadoutSection })));
const CodexSection     = lazy(() => import('./sections/CodexSection').then((m) => ({ default: m.CodexSection })));
const SettingsSection  = lazy(() => import('./sections/SettingsSection').then((m) => ({ default: m.SettingsSection })));

const ORDER: Record<SectionId, number> = SECTIONS.reduce(
  (acc, s, i) => {
    acc[s.id] = i;
    return acc;
  },
  {} as Record<SectionId, number>,
);

export function MainMenu() {
  const [active, setActive] = useState<SectionId>('home');
  const prevRef = useRef<SectionId>('home');

  const direction =
    ORDER[active] > ORDER[prevRef.current] ? 'left' : 'right';

  const handleSelect = (next: SectionId) => {
    if (next === active) return;
    prevRef.current = active;
    setActive(next);
    if (next === 'stats') markStatsTabViewed();
    if (next === 'codex') markCodexTabViewed();
  };

  const swipe = useSwipeNav({
    onSwipeLeft: () => {
      const next = SECTIONS[ORDER[active] + 1];
      if (next) handleSelect(next.id);
    },
    onSwipeRight: () => {
      const prev = SECTIONS[ORDER[active] - 1];
      if (prev) handleSelect(prev.id);
    },
  });

  const animClass = direction === 'left' ? 'section-enter-left' : 'section-enter-right';

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    }}>
      {/* Swipe handlers sit on the scroll container so direction-lock protects
          native vertical scroll: if the gesture starts vertical the hook aborts
          immediately and touch/pointer scrolling proceeds uninterrupted. */}
      <div
        style={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
          // Tells the browser: native vertical pan is OK, horizontal goes to our
          // JS pointer handlers. More reliable than relying on global CSS rules.
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          WebkitOverflowScrolling: 'touch' as any,
        }}
        onPointerDown={swipe.onPointerDown}
        onPointerMove={swipe.onPointerMove}
        onPointerUp={swipe.onPointerUp}
        onPointerCancel={swipe.onPointerCancel}
        onClickCapture={swipe.onClickCapture}
      >
        {/* Key on `active` so React unmounts/remounts on change; that's what re-fires the animation. */}
        <div key={active} className={animClass} style={{ minHeight: '100%' }}>
          {active === 'home' && <HomeSection />}
          {active === 'stats' && <Suspense fallback={<SectionFallback />}><StatsSection /></Suspense>}
          {active === 'hideout' && <Suspense fallback={<SectionFallback />}><HideoutSection /></Suspense>}
          {active === 'sectors' && <Suspense fallback={<SectionFallback />}><SectorsSection /></Suspense>}
          {active === 'inventory' && <Suspense fallback={<SectionFallback />}><InventorySection /></Suspense>}
          {active === 'loadout' && <Suspense fallback={<SectionFallback />}><LoadoutSection /></Suspense>}
          {active === 'codex' && <Suspense fallback={<SectionFallback />}><CodexSection /></Suspense>}
          {active === 'settings' && <Suspense fallback={<SectionFallback />}><SettingsSection /></Suspense>}
        </div>
      </div>
      <SectionNav active={active} onSelect={handleSelect} />
    </div>
  );
}
